import { useEffect, useState, useCallback } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Loader2, Check, Filter, ImageIcon, Trash2, X, AlertTriangle, Copy, Pencil, ArrowUp, ArrowDown, ArrowUpDown, ImagePlus, Share2, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ProductDrawer from "@/components/ProductDrawer";
import SupplierPicker from "@/components/SupplierPicker";
import CategoryPicker from "@/components/CategoryPicker";
import BulkImageFinder from "@/components/BulkImageFinder";
import { money, tagMeta, cogsOf, marginPct } from "@/lib/helpers";
import { useAuth } from "@/context/AuthContext";

const NONE = "__none__";
const ALL = "__all__";

const EMPTY = { name: "", description: "", category_id: null, status_id: NONE, approver_ids: [], supplier_id: null, product_url: "", shelf: "", rrp: "", costs: [] };

function ProductImage({ image, className }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u, active = true;
    if (image?.storage_path) {
      fetch(`${API}/files/${image.storage_path}`, { credentials: "include" }).then((r) => r.blob()).then((b) => { if (active) { u = URL.createObjectURL(b); setUrl(u); } });
    }
    return () => { active = false; if (u) URL.revokeObjectURL(u); };
  }, [image?.storage_path]);
  if (!image) return null;
  return url ? <img src={url} alt="product" className={className} /> : <div className={`${className} flex items-center justify-center bg-zinc-100`}><Loader2 className="h-3 w-3 animate-spin text-zinc-400" /></div>;
}

function TagBadge({ tag }) {
  const m = tagMeta(tag);
  if (!m) return <span className="text-xs text-zinc-300">—</span>;
  return <span className="inline-block border px-2 py-0.5 text-xs font-medium uppercase tracking-wider" style={{ color: m.color, borderColor: m.color, background: m.bg }}>{m.label}</span>;
}

export default function Products() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "manager";
  const [products, setProducts] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [filterCat, setFilterCat] = useState(ALL);
  const [marginThreshold, setMarginThreshold] = useState(20);
  const [lowOnly, setLowOnly] = useState(false);
  const [marginSort, setMarginSort] = useState(null);
  const [selected, setSelected] = useState([]);
  const [bulkImgOpen, setBulkImgOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareTitle, setShareTitle] = useState("Product Portfolio");
  const [shareUrl, setShareUrl] = useState("");
  const [creatingShare, setCreatingShare] = useState(false);
  const toggleSel = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const loadSuppliers = useCallback(async () => { const s = await api.get("/suppliers"); setSuppliers(s.data); }, []);
  const loadCategories = useCallback(async () => { const c = await api.get("/product-categories"); setCategories(c.data); }, []);
  const loadRef = useCallback(async () => {
    const [s, u] = await Promise.all([api.get("/product-statuses"), api.get("/users")]);
    setStatuses(s.data); setUsers(u.data);
  }, []);
  const loadProducts = useCallback(async () => { const p = await api.get("/products"); setProducts(p.data); setLoading(false); }, []);
  useEffect(() => { loadRef(); loadProducts(); loadSuppliers(); loadCategories(); }, [loadRef, loadProducts, loadSuppliers, loadCategories]);
  useEffect(() => { api.get("/settings/margin-threshold").then((r) => setMarginThreshold(r.data.value)).catch(() => {}); }, []);
  const saveThreshold = async (v) => { const val = Number.isFinite(v) ? v : 0; setMarginThreshold(val); try { await api.put("/settings/margin-threshold", { value: val }); } catch (e) {} };

  const [form, setForm] = useState(EMPTY);
  const [imageFile, setImageFile] = useState(null);
  const [importImg, setImportImg] = useState(false);
  const [saving, setSaving] = useState(false);

  const toggleApprover = (id) => setForm((f) => ({ ...f, approver_ids: f.approver_ids.includes(id) ? f.approver_ids.filter((x) => x !== id) : [...f.approver_ids, id] }));
  const addCost = () => setForm((f) => ({ ...f, costs: [...f.costs, { label: "", amount: "" }] }));
  const updCost = (i, k, v) => setForm((f) => ({ ...f, costs: f.costs.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)) }));
  const rmCost = (i) => setForm((f) => ({ ...f, costs: f.costs.filter((_, idx) => idx !== i) }));

  const create = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const { data: product } = await api.post("/products", {
        name: form.name, description: form.description,
        category_id: form.category_id,
        status_id: form.status_id === NONE ? (statuses[0]?.id || null) : form.status_id,
        approver_ids: form.approver_ids,
        supplier_id: form.supplier_id,
        product_url: form.product_url,
        shelf: form.shelf,
        rrp: form.rrp === "" ? null : parseFloat(form.rrp),
        costs: form.costs.filter((c) => c.label.trim() || c.amount !== "").map((c) => ({ label: c.label || "Cost", amount: parseFloat(c.amount) || 0 })),
      });
      if (imageFile) {
        const fd = new FormData(); fd.append("file", imageFile);
        await api.post(`/products/${product.id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else if (importImg && form.product_url) {
        try { await api.post(`/products/${product.id}/import-image`, { url: form.product_url }); } catch (e) { toast.error("Product created, but image import failed"); }
      }
      toast.success("Product created");
      setCreateOpen(false); setForm(EMPTY); setImageFile(null); setImportImg(false);
      loadProducts();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const duplicateProduct = async (id) => {
    try {
      const { data } = await api.post(`/products/${id}/duplicate`);
      toast.success("Product duplicated");
      await loadProducts();
      setOpenId(data.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product? This cannot be undone.")) return;
    try { await api.delete(`/products/${id}`); toast.success("Product deleted"); setProducts((prev) => prev.filter((p) => p.id !== id)); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openShare = () => { setShareUrl(""); setShareTitle("Product Portfolio"); setShareOpen(true); };
  const createShare = async () => {
    setCreatingShare(true);
    try {
      const { data } = await api.post("/product-shares", { product_ids: selected, title: shareTitle.trim() || "Product Portfolio" });
      setShareUrl(`${window.location.origin}/p/${data.token}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCreatingShare(false); }
  };

  const catName = (id) => categories.find((c) => c.id === id)?.name || "—";
  const status = (id) => statuses.find((s) => s.id === id);
  let visible = filterCat === ALL ? products : products.filter((p) => (filterCat === NONE ? !p.category_id : p.category_id === filterCat));
  if (lowOnly) visible = visible.filter((p) => { const m = marginPct(p.rrp, p.costs); return m != null && m < marginThreshold; });
  if (marginSort) {
    visible = [...visible].sort((a, b) => {
      const ma = marginPct(a.rrp, a.costs), mb = marginPct(b.rrp, b.costs);
      if (ma == null && mb == null) return 0;
      if (ma == null) return 1;
      if (mb == null) return -1;
      return marginSort === "asc" ? ma - mb : mb - ma;
    });
  }
  const createCogs = cogsOf(form.costs.map((c) => ({ amount: parseFloat(c.amount) || 0 })));
  const createMargin = marginPct(form.rrp, form.costs.map((c) => ({ amount: parseFloat(c.amount) || 0 })));

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-200 px-6 py-3">
        <div className="flex items-center gap-3">
          <p className="text-sm text-zinc-500">{visible.length} products</p>
          <div className="flex items-center gap-2">
            <Filter className="h-3.5 w-3.5 text-zinc-400" />
            <Select value={filterCat} onValueChange={setFilterCat}>
              <SelectTrigger data-testid="product-category-filter" className="h-8 w-44 rounded-none text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All categories</SelectItem>
                <SelectItem value={NONE}>Uncategorized</SelectItem>
                {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-400">Margin alert &lt;</span>
            <input type="number" data-testid="margin-threshold-input" value={marginThreshold} onChange={(e) => setMarginThreshold(e.target.value === "" ? "" : parseFloat(e.target.value))} onBlur={(e) => saveThreshold(parseFloat(e.target.value) || 0)} disabled={!canManage} className="h-8 w-16 border border-zinc-200 px-2 text-xs outline-none focus:border-[#e84e34] disabled:bg-zinc-50" />
            <span className="text-xs text-zinc-400">%</span>
          </div>
          <button onClick={() => setLowOnly((v) => !v)} data-testid="low-margin-toggle" className={`flex items-center gap-1.5 border px-3 py-1.5 text-xs font-medium transition-colors ${lowOnly ? "border-[#e84e34] bg-[#e84e34] text-white" : "border-zinc-200 text-zinc-600 hover:border-zinc-300"}`}>
            <AlertTriangle className="h-3.5 w-3.5" /> Low margin
          </button>
          {selected.length > 0 && (
            <>
              <button onClick={() => setBulkImgOpen(true)} data-testid="bulk-find-images-button" className="flex items-center gap-1.5 border border-[#e84e34] bg-[#fdece8] px-3 py-1.5 text-xs font-medium text-[#e84e34] transition-colors hover:bg-[#e84e34] hover:text-white">
                <ImagePlus className="h-3.5 w-3.5" /> Find images ({selected.length})
              </button>
              <button onClick={openShare} data-testid="share-products-button" className="flex items-center gap-1.5 bg-[#e84e34] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[#c73e26]">
                <Share2 className="h-3.5 w-3.5" /> Share ({selected.length})
              </button>
            </>
          )}
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <button data-testid="create-product-button" className="flex items-center gap-2 bg-[#e84e34] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#c73e26]">
              <Plus className="h-4 w-4" /> New Product
            </button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto rounded-none sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading tracking-tight">Create product</DialogTitle>
              <DialogDescription>Details, supplier, pricing, costs and an image.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <input data-testid="product-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Product name" className="w-full border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-[#e84e34]" />
              <textarea data-testid="product-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" rows={2} className="w-full border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-[#e84e34]" />
              <div className="grid grid-cols-2 gap-3">
                <CategoryPicker value={form.category_id} onChange={(id) => setForm({ ...form, category_id: id })} categories={categories} reload={loadCategories} canAdd={canManage} />
                <div>
                  <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Status</label>
                  <Select value={form.status_id} onValueChange={(v) => setForm({ ...form, status_id: v })}>
                    <SelectTrigger data-testid="product-status-select" className="rounded-none"><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Default status</SelectItem>
                      {statuses.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <SupplierPicker value={form.supplier_id} onChange={(id) => setForm({ ...form, supplier_id: id })} suppliers={suppliers} reload={loadSuppliers} />
                <div>
                  <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Shelf (in store)</label>
                  <input data-testid="product-shelf-input" value={form.shelf} onChange={(e) => setForm({ ...form, shelf: e.target.value })} placeholder="e.g. A3" className="w-full border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-[#e84e34]" />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Product URL</label>
                <input data-testid="product-url-input" value={form.product_url} onChange={(e) => setForm({ ...form, product_url: e.target.value })} placeholder="https://…" className="w-full border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-[#e84e34]" />
              </div>

              {/* Pricing & COGS */}
              <div className="border border-zinc-200 p-3">
                <p className="mb-2 text-xs font-medium uppercase tracking-wider text-zinc-500">Pricing & margin</p>
                <label className="mb-1 block text-[11px] text-zinc-400">Selling price / RRP ($)</label>
                <input type="number" step="0.01" data-testid="product-rrp-input" value={form.rrp} onChange={(e) => setForm({ ...form, rrp: e.target.value })} placeholder="0.00" className="mb-3 w-full border border-zinc-200 px-2 py-1.5 text-sm outline-none focus:border-[#e84e34]" />
                <div className="flex items-center justify-between">
                  <span className="text-[11px] text-zinc-400">Costs (COGS)</span>
                  <button type="button" onClick={addCost} data-testid="add-cost-button" className="flex items-center gap-1 text-xs font-medium text-[#e84e34] hover:underline"><Plus className="h-3 w-3" /> Add cost</button>
                </div>
                <div className="mt-1 space-y-2">
                  {form.costs.map((c, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`cost-row-${i}`}>
                      <input value={c.label} onChange={(e) => updCost(i, "label", e.target.value)} data-testid={`cost-label-${i}`} placeholder="Cost name" className="flex-1 border border-zinc-200 px-2 py-1.5 text-sm outline-none focus:border-[#e84e34]" />
                      <input type="number" step="0.01" value={c.amount} onChange={(e) => updCost(i, "amount", e.target.value)} data-testid={`cost-amount-${i}`} placeholder="0.00" className="w-24 border border-zinc-200 px-2 py-1.5 text-sm outline-none focus:border-[#e84e34]" />
                      <button type="button" onClick={() => rmCost(i)} className="text-zinc-300 hover:text-[#e84e34]"><X className="h-4 w-4" /></button>
                    </div>
                  ))}
                  {form.costs.length === 0 && <p className="text-xs text-zinc-400">No costs added</p>}
                </div>
                {(form.rrp !== "" || form.costs.length > 0) && (
                  <div className="mt-2 flex gap-4 border-t border-zinc-100 pt-2 text-xs">
                    <span className="text-zinc-500">COGS: <span className="font-medium text-zinc-800">{money(createCogs)}</span></span>
                    <span className="text-zinc-500">Profit: <span className="font-medium text-zinc-800">{form.rrp !== "" ? money(parseFloat(form.rrp) - createCogs) : "—"}</span></span>
                    <span className="text-zinc-500">Margin: <span className="font-medium" style={{ color: createMargin != null && createMargin < 0 ? "#e84e34" : "#16a34a" }}>{createMargin != null ? `${createMargin.toFixed(1)}%` : "—"}</span></span>
                  </div>
                )}
              </div>

              {/* Image */}
              <div className="border border-zinc-200 p-3">
                <p className="mb-2 text-xs font-medium uppercase tracking-wider text-zinc-500">Product image</p>
                <input type="file" accept="image/*" data-testid="product-image-input" onChange={(e) => { setImageFile(e.target.files?.[0] || null); setImportImg(false); }} className="block w-full text-xs text-zinc-600 file:mr-3 file:border file:border-zinc-200 file:bg-zinc-50 file:px-3 file:py-1.5 file:text-xs" />
                <label className="mt-2 flex items-center gap-2 text-xs text-zinc-600">
                  <input type="checkbox" data-testid="product-import-image" checked={importImg} disabled={!form.product_url || !!imageFile} onChange={(e) => setImportImg(e.target.checked)} />
                  Import image from the product URL instead
                </label>
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Approvers</label>
                <div className="flex max-h-28 flex-wrap gap-2 overflow-y-auto border border-zinc-200 p-2">
                  {users.map((u) => (
                    <button key={u.id} type="button" data-testid={`approver-toggle-${u.id}`} onClick={() => toggleApprover(u.id)} className={`flex items-center gap-1 border px-2 py-1 text-xs transition-colors ${form.approver_ids.includes(u.id) ? "border-[#e84e34] bg-[#fdece8] text-[#e84e34]" : "border-zinc-200 text-zinc-600 hover:border-zinc-300"}`}>
                      {form.approver_ids.includes(u.id) && <Check className="h-3 w-3" />} {u.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <button data-testid="product-submit-button" onClick={create} disabled={saving} className="flex items-center gap-2 bg-[#e84e34] px-4 py-2 text-sm font-medium text-white hover:bg-[#c73e26] disabled:opacity-60">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create product
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#e84e34]" /></div>
      ) : visible.length === 0 ? (
        <div className="flex flex-1 items-center justify-center text-sm text-zinc-400">No products yet. Create your first one.</div>
      ) : (
        <div className="flex-1 overflow-auto p-6">
          <table className="w-full border-collapse border border-zinc-200 bg-white" data-testid="products-table">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs font-medium uppercase tracking-wider text-zinc-500">
                <th className="w-10 px-4 py-3"><input type="checkbox" data-testid="select-all-products" checked={visible.length > 0 && selected.length === visible.length} onChange={(e) => setSelected(e.target.checked ? visible.map((p) => p.id) : [])} /></th>
                <th className="px-4 py-3">Product</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">RRP</th>
                <th className="px-4 py-3">
                  <button data-testid="margin-sort-header" onClick={() => setMarginSort((s) => (s === "desc" ? "asc" : s === "asc" ? null : "desc"))} className="flex items-center gap-1 uppercase tracking-wider hover:text-[#e84e34]">
                    Margin
                    {marginSort === "desc" ? <ArrowDown className="h-3 w-3" /> : marginSort === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowUpDown className="h-3 w-3 text-zinc-300" />}
                  </button>
                </th>
                <th className="px-4 py-3">Tag</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((p) => {
                const st = status(p.status_id);
                const m = marginPct(p.rrp, p.costs);
                return (
                  <tr key={p.id} data-testid={`product-row-${p.id}`} onClick={() => setOpenId(p.id)} className="cursor-pointer border-b border-zinc-100 transition-colors hover:bg-zinc-50">
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}><input type="checkbox" data-testid={`select-product-${p.id}`} checked={selected.includes(p.id)} onChange={() => toggleSel(p.id)} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {p.image ? <ProductImage image={p.image} className="h-10 w-10 flex-shrink-0 border border-zinc-200 object-cover" /> : <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center border border-zinc-200 bg-zinc-50 text-zinc-300"><ImageIcon className="h-4 w-4" /></div>}
                        <div>
                          <div className="text-sm font-medium text-zinc-900">{p.name}</div>
                          <div className="text-xs text-zinc-400">{p.supplier?.company_name || "No supplier"}{p.shelf ? ` · Shelf ${p.shelf}` : ""}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-zinc-600">{catName(p.category_id)}</td>
                    <td className="px-4 py-3 text-sm text-zinc-700">{money(p.rrp)}</td>
                    <td className="px-4 py-3 text-sm">{m != null ? (
                      <span className="inline-flex items-center gap-1" style={{ color: m < marginThreshold ? "#e84e34" : "#16a34a" }}>
                        {m < marginThreshold && <AlertTriangle className="h-3.5 w-3.5" data-testid={`low-margin-flag-${p.id}`} />}
                        {m.toFixed(1)}%
                      </span>
                    ) : <span className="text-zinc-300">—</span>}</td>
                    <td className="px-4 py-3"><TagBadge tag={p.tag} /></td>
                    <td className="px-4 py-3">{st ? <span className="inline-flex items-center gap-1.5 text-sm text-zinc-700"><span className="h-2 w-2 rounded-full" style={{ background: st.color }} /> {st.name}</span> : "—"}</td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-2">
                        <button data-testid={`edit-product-row-${p.id}`} onClick={() => setOpenId(p.id)} title="Edit product" className="inline-flex items-center gap-1 border border-zinc-200 px-2 py-1 text-xs text-zinc-500 transition-colors hover:border-[#e84e34] hover:text-[#e84e34]"><Pencil className="h-3.5 w-3.5" /> Edit</button>
                        {canManage && <button data-testid={`duplicate-product-row-${p.id}`} onClick={() => duplicateProduct(p.id)} title="Duplicate product" className="inline-flex items-center gap-1 border border-zinc-200 px-2 py-1 text-xs text-zinc-500 transition-colors hover:border-[#e84e34] hover:text-[#e84e34]"><Copy className="h-3.5 w-3.5" /> Duplicate</button>}
                        {canManage && <button data-testid={`delete-product-row-${p.id}`} onClick={() => deleteProduct(p.id)} title="Delete product" className="inline-flex items-center gap-1 border border-zinc-200 px-2 py-1 text-xs text-zinc-500 transition-colors hover:border-[#e84e34] hover:text-[#e84e34]"><Trash2 className="h-3.5 w-3.5" /> Delete</button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {openId && <ProductDrawer productId={openId} onClose={() => setOpenId(null)} statuses={statuses} categories={categories} users={users} suppliers={suppliers} reloadSuppliers={loadSuppliers} reloadCategories={loadCategories} onChange={loadProducts} />}
      <BulkImageFinder open={bulkImgOpen} onOpenChange={setBulkImgOpen} entity="products" items={visible.filter((p) => selected.includes(p.id)).map((p) => ({ id: p.id, name: p.name }))} onDone={loadProducts} />

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="rounded-none sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Share product portfolio</DialogTitle>
            <DialogDescription>Creates a public link with a visual card for each selected product (RRP, Cost, Profit, Margin).</DialogDescription>
          </DialogHeader>
          {!shareUrl ? (
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Portfolio title</label>
                <input data-testid="share-title-input" value={shareTitle} onChange={(e) => setShareTitle(e.target.value)} className="w-full border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-[#e84e34]" />
              </div>
              <p className="text-xs text-zinc-400">{selected.length} product{selected.length === 1 ? "" : "s"} will be included.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <input readOnly data-testid="share-url-input" value={shareUrl} onFocus={(e) => e.target.select()} className="flex-1 border border-zinc-200 bg-zinc-50 px-2 py-2 text-xs outline-none" />
                <button onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); }} data-testid="copy-share-link" className="flex items-center gap-1 border border-zinc-200 px-2 py-2 text-xs text-zinc-600 hover:border-[#e84e34]"><Copy className="h-3.5 w-3.5" /> Copy</button>
              </div>
              <a href={shareUrl} target="_blank" rel="noreferrer" data-testid="open-share-link" className="inline-flex items-center gap-1 text-sm font-medium text-[#e84e34] hover:underline"><ExternalLink className="h-3.5 w-3.5" /> Open portfolio</a>
            </div>
          )}
          <DialogFooter>
            {!shareUrl && <button onClick={createShare} disabled={creatingShare} data-testid="create-share-link" className="flex items-center gap-2 bg-[#e84e34] px-4 py-2 text-sm font-medium text-white hover:bg-[#c73e26] disabled:opacity-60">{creatingShare && <Loader2 className="h-4 w-4 animate-spin" />} Create link</button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
